import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { casesContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function CasesSection() {
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();

  return (
    <section className="relative bg-background-100 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        {/* Title */}
        <div className={`mb-10 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
            {casesContent.titleEn}
          </span>
          <h2 className="mt-1 text-2xl md:text-3xl lg:text-4xl font-black text-foreground-50">
            {casesContent.titleCn}
          </h2>
          <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
        </div>

        {/* Cases Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {casesContent.items.map((item, index) => (
            <Link
              to={`/cases/${item.slug}`}
              key={item.id}
              className={`group relative rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-500/40 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${150 + index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.company}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              </div>

              {/* Overlay text */}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <span className="inline-block px-2 py-0.5 rounded bg-primary-500/80 text-white text-[10px] font-medium mb-1.5">
                  {item.industry}
                </span>
                <h4 className="text-base md:text-lg font-bold text-white">
                  {item.company}
                </h4>
                <p className="mt-1 text-xs md:text-sm text-white/70 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* View All */}
        <div className={`mt-10 md:mt-14 text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '600ms' }}>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            查看全部案例
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}